import React, { forwardRef, useImperativeHandle } from 'react';
import { DetectAbnormalLayoutRequest } from '../types';
import { features } from '../config';

const DEFAULT_CUSTOM_INSTRUCTIONS = `# Important Notes

1. **DO NOT flag as abnormal layout just because of the below reasons:**
- the receipt total amount or line item prices is much higher/lower than the reference samples
- the receipt contains handwriting marks or highlighting that are not seen in the reference samples
- membership information or member point information is missing in the receipt
- some receipt information is not visible due to folded receipt or cropped image
- duplicated invoice number
- calculation error

2. **DO NOT rely on handwritten marks or highlighting to detect abnormal layout.**
- e.g. all reference samples show a handwritten note, but the input sample does not. This is normal in receipts and DO NOT indicate abnormal layout.

3. Tolerate for layout variations due to the membership status of the customer.
- e.g. for guest customers, the receipt may show promotional messages to encourage them to sign up for membership. For members, the receipt may show member point information.`;

export interface FormDataWithAuth extends DetectAbnormalLayoutRequest {
  auth_token: string;
  chat_complete_request_args_enabled?: boolean;
  custom_instructions_enabled?: boolean;
}

interface DetectAbnormalLayoutRequestFormProps {
  formData: FormDataWithAuth;
  onFormDataChange: (formData: FormDataWithAuth) => void;
  onSubmit: (request: DetectAbnormalLayoutRequest, authToken: string) => void;
  disabled?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

export interface DetectAbnormalLayoutRequestFormRef {
  isValid: () => boolean;
}

export const DetectAbnormalLayoutRequestForm = forwardRef<DetectAbnormalLayoutRequestFormRef, DetectAbnormalLayoutRequestFormProps>(({ 
  formData,
  onFormDataChange,
  onSubmit,
  disabled,
  onValidationChange
}, ref) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { auth_token, ...requestData } = formData;
    // Only include args in the request when enabled; otherwise send null
    const payload = {
      ...requestData,
      chat_complete_request_args: chatArgsEnabled ? requestData.chat_complete_request_args : null,
      custom_instructions: customInstructionsEnabled ? requestData.custom_instructions : null,
    };
    onSubmit(payload, auth_token);
  };

  const updateFormData = (updates: Partial<FormDataWithAuth>) => {
    onFormDataChange({ ...formData, ...updates });
  };

  // Custom Chat Completion Args (JSON) state
  const [chatArgsEnabled, setChatArgsEnabled] = React.useState(
    formData.chat_complete_request_args_enabled ?? !!formData.chat_complete_request_args
  );
  const [chatArgsText, setChatArgsText] = React.useState(
    formData.chat_complete_request_args ? JSON.stringify(formData.chat_complete_request_args, null, 2) : ''
  );
  const [chatArgsError, setChatArgsError] = React.useState<string | null>(null);

  const handleChatArgsChange = (text: string) => {
    setChatArgsText(text);
    if (text.trim() === '') {
      setChatArgsError(null);
      // Keep cache empty if user clears it explicitly
      updateFormData({ 
        chat_complete_request_args: null,
        chat_complete_request_args_enabled: chatArgsEnabled
      });
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setChatArgsError(null);
        updateFormData({ 
          chat_complete_request_args: parsed,
          chat_complete_request_args_enabled: chatArgsEnabled
        });
      } else {
        setChatArgsError('JSON must be an object (e.g., { "temperature": 0.2 })');
      }
    } catch {
      setChatArgsError('Invalid JSON');
    }
  };

  const handleChatArgsToggle = () => {
    const newEnabled = !chatArgsEnabled;
    setChatArgsEnabled(newEnabled);
    if (!newEnabled) {
      // Disable: keep the text/cache but send null
      updateFormData({ 
        chat_complete_request_args: null,
        chat_complete_request_args_enabled: false
      });
    } else {
      // Enable: restore from cache if valid
      if (chatArgsText.trim() !== '') {
        try {
          const parsed = JSON.parse(chatArgsText);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            updateFormData({ 
              chat_complete_request_args: parsed,
              chat_complete_request_args_enabled: true
            });
            setChatArgsError(null);
          }
        } catch {
          // Keep as is if invalid
          updateFormData({ chat_complete_request_args_enabled: true });
        }
      } else {
        updateFormData({ chat_complete_request_args_enabled: true });
      }
    }
  };

  // Custom Instructions state
  const [customInstructionsEnabled, setCustomInstructionsEnabled] = React.useState(
    formData.custom_instructions_enabled ?? true
  );
  const [customInstructionsText, setCustomInstructionsText] = React.useState(
    formData.custom_instructions || ''
  );

  const handleCustomInstructionsChange = (text: string) => {
    setCustomInstructionsText(text);
    updateFormData({ 
      custom_instructions: text,
      custom_instructions_enabled: customInstructionsEnabled
    });
  };

  const handleCustomInstructionsToggle = () => {
    const newEnabled = !customInstructionsEnabled;
    setCustomInstructionsEnabled(newEnabled);
    if (!newEnabled) {
      // Disable: keep the text/cache but send null
      updateFormData({ 
        custom_instructions: null,
        custom_instructions_enabled: false
      });
    } else {
      // Enable: restore from cache
      updateFormData({ 
        custom_instructions: customInstructionsText,
        custom_instructions_enabled: true
      });
    }
  };

  const handleLoadTemplate = (template: 'default') => {
    if (template === 'default') {
      setCustomInstructionsText(DEFAULT_CUSTOM_INSTRUCTIONS);
      updateFormData({ 
        custom_instructions: DEFAULT_CUSTOM_INSTRUCTIONS,
        custom_instructions_enabled: customInstructionsEnabled
      });
    }
  };

  // Validation
  const isValid = React.useMemo(() => {
    // Merchant name is now optional (validation handled at container level)
    return !chatArgsEnabled || !chatArgsError;
  }, [chatArgsEnabled, chatArgsError]);

  // Expose isValid via ref
  useImperativeHandle(ref, () => ({
    isValid: () => isValid,
  }));

  // Notify parent of validation changes
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  return (
    <form onSubmit={handleSubmit} className="request-form">
      {/* LLM Configuration */}
      <div className="form-section">
        <h3>LLM Configuration</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="llm-provider">LLM Provider *</label>
            <select
              id="llm-provider"
              value={formData.llm_model_provider}
              onChange={(e) => updateFormData({ llm_model_provider: e.target.value as 'openrouter' | 'google' })}
              disabled={disabled}
              required
            >
              <option value="openrouter">OpenRouter</option>
              <option value="google">Google</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="llm-model">Model Name</label>
            <input
              id="llm-model"
              type="text"
              value={formData.llm_model_name || ''}
              onChange={(e) => updateFormData({ llm_model_name: e.target.value || null })}
              placeholder="e.g., google/gemini-2.0-flash-exp:free"
              disabled={disabled}
            />
            <div className="form-help-text">
              <small>Leave empty to use provider's default model</small>
            </div>
          </div>
        </div>

        {formData.llm_model_provider === 'openrouter' && (
          <div className="form-group">
            <label htmlFor="openrouter-key">OpenRouter API Key</label>
            <input
              id="openrouter-key"
              type="password"
              value={formData.openrouter_api_key || ''}
              onChange={(e) => updateFormData({ openrouter_api_key: e.target.value || null })}
              placeholder="sk-or-..."
              disabled={disabled}
            />
            <div className="form-help-text">
              <small>Optional: Only required if not configured on server</small>
            </div>
          </div>
        )}

        {features.enableAdvancedOptions && (
          <div className="form-group">
            <div className="toggle-label">
              <label htmlFor="chat-args-toggle">Custom Chat Completion Arguments</label>
              <label className="toggle-switch">
                <input
                  id="chat-args-toggle"
                  type="checkbox"
                  className="toggle-input"
                  checked={chatArgsEnabled}
                  onChange={handleChatArgsToggle}
                  disabled={disabled}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            {chatArgsEnabled && (
              <>
                <textarea
                  value={chatArgsText}
                  onChange={(e) => handleChatArgsChange(e.target.value)}
                  placeholder='{ "temperature": 0.2, "max_tokens": 1000 }'
                  rows={4}
                  disabled={disabled}
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                {chatArgsError && (
                  <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    {chatArgsError}
                  </div>
                )}
                <div className="form-help-text">
                  <small>Additional arguments to pass to the LLM chat completion API (JSON object)</small>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Authentication */}
      <div className="form-section">
        <h3>Authentication</h3>
        
        <div className="form-group">
          <label htmlFor="auth-token">Authorization Token</label>
          <input
            id="auth-token"
            type="password"
            value={formData.auth_token}
            onChange={(e) => updateFormData({ auth_token: e.target.value })}
            placeholder="Bearer token (if required by server)"
            disabled={disabled}
          />
          <div className="form-help-text">
            <small>Leave empty if server doesn't require authentication</small>
          </div>
        </div>
      </div>

      {/* Custom Instructions */}
      <div className="form-section">
        <h3>Custom Instructions</h3>
        
        <div className="form-group">
          <div className="toggle-label">
            <label htmlFor="custom-instructions-toggle">Enable Custom Instructions</label>
            <label className="toggle-switch">
              <input
                id="custom-instructions-toggle"
                type="checkbox"
                className="toggle-input"
                checked={customInstructionsEnabled}
                onChange={handleCustomInstructionsToggle}
                disabled={disabled}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          {customInstructionsEnabled && (
            <>
              <div className="form-help-text" style={{ marginBottom: '0.5rem' }}>
                <small>Load template:</small>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleLoadTemplate('default')}
                    disabled={disabled}
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                  >
                    Default
                  </button>
                </div>
              </div>
              <textarea
                value={customInstructionsText}
                onChange={(e) => handleCustomInstructionsChange(e.target.value)}
                placeholder="Enter custom instructions for abnormal layout detection..."
                rows={10}
                disabled={disabled}
                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              />
              <div className="form-help-text">
                <small>Provide custom instructions to guide the abnormal layout detection process</small>
              </div>
            </>
          )}
        </div>
      </div>
    </form>
  );
});

DetectAbnormalLayoutRequestForm.displayName = 'DetectAbnormalLayoutRequestForm';
